import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, Calendar, MessageSquare, ArrowRight, CheckCircle, 
  Zap, Users, Clock, Shield, Sparkles, PlayCircle,
  Building2, ChevronRight, Bot, TrendingUp, PhoneCall, Star, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import AIChatbot from '../components/AIChatbot';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-slate-900">
              Front Desk <span className="text-blue-600">Pro</span>
            </span>
          </div>
          
          <div className="hidden sm:flex items-center gap-6">
            <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Features</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Pricing</a>
            <a href="#compare" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Compare</a>
          </div>
          
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Button 
                onClick={() => navigate(user?.role === 'admin' ? '/admin' : user?.role === 'sales_rep' ? '/sales' : user?.business_id ? `/business/${user.business_id}` : '/pricing')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600 hover:text-slate-900">
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div>
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <Shield className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700 font-medium">Trusted by 500+ Businesses</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Never Miss a Call.
                <br />
                <span className="text-blue-600">Never Lose a Customer.</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 max-w-xl">
                Build an AI receptionist that actually knows your business—your prices, 
                your services, your policies. It answers like you trained it yourself.
              </p>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900">24/7</div>
                    <div className="text-xs text-slate-500">Availability</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900">100+</div>
                    <div className="text-xs text-slate-500">Simultaneous Calls</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900">500+</div>
                    <div className="text-xs text-slate-500">Businesses Trust Us</div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  size="lg"
                  onClick={() => navigate('/signup')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 text-base"
                  data-testid="hero-cta-btn"
                >
                  <PlayCircle className="w-5 h-5 mr-2" />
                  Try Free Demo
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 h-12 text-base"
                >
                  View Pricing <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </div>

              {/* Trust Line */}
              <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  No credit card required
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Setup in minutes
                </span>
              </div>
            </div>

            {/* Right Column - Demo Card */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl blur-2xl" />
              <Card className="relative bg-white border-slate-200 shadow-xl shadow-slate-200/50 rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-slate-900 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-medium">AI Receptionist</div>
                      <div className="text-xs text-green-400 flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        Active Now
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conversation */}
                <div className="p-6 space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl rounded-tl-none p-3 text-slate-700 text-sm">
                      "Thanks for calling Smith Plumbing! This is Sophie. How can I help you?"
                    </div>
                  </div>
                  
                  <div className="flex gap-3 justify-end">
                    <div className="bg-blue-600 rounded-2xl rounded-tr-none p-3 text-white text-sm max-w-xs">
                      "How much do you charge for a drain cleaning?"
                    </div>
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-slate-600" />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl rounded-tl-none p-3 text-slate-700 text-sm">
                      "Our drain cleaning starts at $150 for standard clogs. For main line issues, it's $275. Would you like me to get you on the schedule?"
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
                  <Button 
                    onClick={() => navigate('/signup')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Try It Free
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* The Problem */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-100 mb-4">
                <span className="text-sm text-red-600 font-medium">The Problem</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                You're Losing Customers Every Day
              </h3>
              <ul className="space-y-3">
                {[
                  'Missed calls while you\'re on a job or with another customer',
                  'Voicemails that never get returned in time',
                  'Competitors answering their phones when you can\'t',
                  'No one to answer after hours, weekends, or holidays',
                  'Customers hanging up when they can\'t get quick answers'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600">
                    <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* The Solution */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-100 mb-4">
                <span className="text-sm text-green-600 font-medium">The Solution</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                An AI That Works Like Your Best Employee
              </h3>
              <ul className="space-y-3">
                {[
                  'Answers every call instantly, 24/7/365',
                  'Knows your prices, services, and policies by heart',
                  'Connects to your systems—checks inventory, availability in real-time',
                  'Sounds natural—callers think it\'s a real person',
                  'Takes messages and books appointments',
                  'Transfers urgent calls directly to your phone'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              An AI That Actually Knows Your Business
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Train it once with your prices, services, and policies. It handles the rest.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Brain,
                title: 'Knows Your Pricing',
                description: 'Tell it your rates once. When customers ask "how much?", it answers correctly every time.',
                color: 'bg-blue-50 text-blue-600'
              },
              {
                icon: Building2,
                title: 'Learns Your Business',
                description: 'Services, hours, policies, FAQs—your AI knows it all and answers like a trained employee.',
                color: 'bg-green-50 text-green-600'
              },
              {
                icon: Zap,
                title: 'Connects to Your Systems',
                description: 'Link your inventory, scheduling, or CRM. AI tells customers what\'s in stock or available.',
                color: 'bg-yellow-50 text-yellow-600'
              },
              {
                icon: PhoneCall,
                title: 'Smart Call Transfers',
                description: 'Urgent issue? AI transfers to you instantly. Routine question? Handled automatically.',
                color: 'bg-orange-50 text-orange-600'
              },
              {
                icon: Clock,
                title: '24/7 Availability',
                description: 'Nights, weekends, holidays. Every call answered professionally, no exceptions.',
                color: 'bg-purple-50 text-purple-600'
              },
              {
                icon: TrendingUp,
                title: 'Every Call Recorded',
                description: 'Full transcripts, recordings, and analytics. Know exactly what your customers are asking.',
                color: 'bg-cyan-50 text-cyan-600'
              }
            ].map((feature, i) => (
              <Card key={i} className="bg-white border-slate-200 p-6 hover:shadow-lg hover:shadow-slate-100 transition-all">
                <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Your AI Receptionist in 3 Simple Steps
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              No tech skills needed. No complicated setup. Just answer a few questions and you're live.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold">
                1
              </div>
              <Card className="bg-slate-800 border-slate-700 p-6 pt-10 h-full">
                <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <Building2 className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Tell Us About Your Business</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  What services do you offer? What are your prices? What hours are you open? 
                  We ask you simple questions and build your AI's knowledge base.
                </p>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-xs text-slate-500">The more detail, the smarter your AI</p>
                </div>
              </Card>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold">
                2
              </div>
              <Card className="bg-slate-800 border-slate-700 p-6 pt-10 h-full">
                <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                  <Phone className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Get Your Dedicated Number</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Choose a local phone number for your AI receptionist. Forward your existing line 
                  or use it as a new business number—your choice.
                </p>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-xs text-slate-500">Pick your area code</p>
                </div>
              </Card>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold">
                3
              </div>
              <Card className="bg-slate-800 border-slate-700 p-6 pt-10 h-full">
                <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <Sparkles className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Your AI Goes Live</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  That's it. Your AI answers calls, quotes your prices, explains your services, 
                  and transfers urgent calls to you. Review every conversation in your dashboard.
                </p>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-xs text-slate-500">You built the brain—now it works for you</p>
                </div>
              </Card>
            </div>
          </div>

          {/* What You Get Box */}
          <div className="mt-16 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-white mb-6 text-center">What You Get</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Phone, text: 'Your own AI phone number' },
                { icon: Brain, text: 'AI trained on YOUR business' },
                { icon: Clock, text: '24/7 call answering' },
                { icon: MessageSquare, text: 'Every call recorded & transcribed' },
                { icon: PhoneCall, text: 'Urgent call transfers to you' },
                { icon: Users, text: 'Handle 100+ calls at once' },
                { icon: TrendingUp, text: 'Dashboard with full analytics' },
                { icon: Shield, text: 'Change settings anytime' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Button 
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-10 h-14 text-lg"
            >
              Build Your AI Receptionist
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-slate-500 text-sm mt-4">No credit card required to start</p>
          </div>
        </div>
      </section>

      {/* Comparison Section - Redesigned */}
      <section id="compare" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              The Old Way vs. The Future
            </h2>
            <p className="text-lg text-slate-600">Your competitors are still hiring. You're building.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Human Receptionist - The Old Way */}
            <Card className="bg-white border-slate-200 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-slate-200 text-slate-600 text-xs font-medium px-4 py-1 rounded-bl-lg">
                Traditional
              </div>
              <h3 className="text-xl font-bold text-slate-400 mb-6 mt-4">Human Receptionist</h3>
              <ul className="space-y-4">
                {[
                  { label: 'Availability', value: '40 hours/week', subtext: 'Closed nights, weekends, holidays' },
                  { label: 'Monthly Cost', value: '$3,500+', subtext: 'Salary, benefits, training, turnover' },
                  { label: 'Calls at Once', value: '1', subtext: 'Others go to voicemail' },
                  { label: 'Business Knowledge', value: 'Varies', subtext: 'Depends on training & memory' },
                  { label: 'Sick Days', value: 'Yes', subtext: 'Plus vacations, breaks, bad days' },
                  { label: 'Scales With You', value: 'No', subtext: 'Hire more = pay more' },
                ].map((item, i) => (
                  <li key={i} className="flex justify-between items-start pb-4 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-slate-900 font-medium">{item.label}</p>
                      <p className="text-xs text-slate-400">{item.subtext}</p>
                    </div>
                    <span className="text-slate-500 font-semibold">{item.value}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Front Desk Pro - The Future */}
            <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 border-0 p-8 relative overflow-hidden shadow-xl shadow-blue-600/20">
              <div className="absolute top-0 right-0 bg-white/20 text-white text-xs font-medium px-4 py-1 rounded-bl-lg">
                AI-Powered
              </div>
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
              <h3 className="text-xl font-bold text-white mb-6 mt-4 flex items-center gap-2">
                Front Desk Pro
                <Sparkles className="w-5 h-5 text-yellow-300" />
              </h3>
              <ul className="space-y-4 relative z-10">
                {[
                  { label: 'Availability', value: '24/7/365', subtext: 'Every call, every time, no exceptions' },
                  { label: 'Monthly Cost', value: 'From $99', subtext: 'Flat rate. No surprises.' },
                  { label: 'Calls at Once', value: 'Unlimited', subtext: 'Handle 100+ simultaneously' },
                  { label: 'Business Knowledge', value: 'Perfect', subtext: 'Knows exactly what you teach it' },
                  { label: 'Sick Days', value: 'Never', subtext: 'Always on, always consistent' },
                  { label: 'Scales With You', value: 'Instantly', subtext: 'Grow without hiring' },
                ].map((item, i) => (
                  <li key={i} className="flex justify-between items-start pb-4 border-b border-white/10 last:border-0">
                    <div>
                      <p className="text-white font-medium">{item.label}</p>
                      <p className="text-xs text-blue-200">{item.subtext}</p>
                    </div>
                    <span className="text-white font-bold">{item.value}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Still paying someone to answer phones?</p>
            <Button 
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              Switch to AI
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">No hidden fees. No contracts. Cancel anytime.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Starter Plan */}
            <Card className="bg-white border-slate-200 p-8 hover:shadow-xl hover:shadow-slate-100 transition-all">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Starter</h3>
                <p className="text-slate-600 text-sm">Perfect for small businesses</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">$99</span>
                <span className="text-slate-500">/month</span>
                <p className="text-sm text-slate-500 mt-1">Includes 150 minutes</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['150 included minutes', '24/7 AI receptionist', 'Call recording & transcripts', 'Basic analytics', 'Email support'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Get Started
              </Button>
            </Card>

            {/* Pro Plan */}
            <Card className="bg-blue-600 border-blue-600 p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-3 py-1 bg-white/20 rounded-full">
                <span className="text-xs font-medium text-white">Most Popular</span>
              </div>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">Pro</h3>
                <p className="text-blue-100 text-sm">For growing businesses</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$199</span>
                <span className="text-blue-200">/month</span>
                <p className="text-sm text-blue-200 mt-1">Includes 400 minutes</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['400 included minutes', 'Everything in Starter', 'Calendar integration', 'Priority support', 'Custom AI personality'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-blue-50">
                    <CheckCircle className="w-4 h-4 text-green-300 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/pricing')}
                className="w-full bg-white text-blue-600 hover:bg-blue-50"
              >
                Get Started
              </Button>
            </Card>
          </div>

          <p className="text-center text-slate-500 text-sm mt-8">
            Need more? <span className="text-blue-600 font-medium cursor-pointer hover:underline" onClick={() => navigate('/contact')}>Contact us</span> for enterprise pricing
          </p>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />)}
          </div>
          <blockquote className="text-2xl sm:text-3xl text-white font-medium mb-8 leading-relaxed">
            "Front Desk Pro has been a game-changer for our plumbing business. 
            We're capturing 40% more leads just from after-hours calls we used to miss."
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xl">
              JS
            </div>
            <div className="text-left">
              <div className="text-white font-semibold">John Smith</div>
              <div className="text-slate-400 text-sm">Owner, Smith Plumbing Co.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Ready to Stop Missing Calls?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join hundreds of businesses that never lose a customer to a missed call.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12"
            >
              <Phone className="w-5 h-5 mr-2" />
              Start Free Trial
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/contact')}
              className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 h-12"
            >
              Talk to Sales
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="mt-10 flex flex-wrap justify-center gap-6 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>SOC 2 Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>99.9% Uptime</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>Setup in 24 Hours</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="text-slate-600 text-sm">
                &copy; 2026 Front Desk Pro. All rights reserved.
              </span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <a href="/terms" className="text-slate-600 hover:text-slate-900 transition-colors">
                Terms
              </a>
              <a href="/privacy" className="text-slate-600 hover:text-slate-900 transition-colors">
                Privacy
              </a>
              <a href="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
      <AIChatbot />
    </div>
  );
};

export default Landing;
